  // Helpers
  const toSlug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  
  // Tree-style helper: lay children in a horizontal row below the parent
  const placeChildrenTree = (parentX, parentY, count, horizontalSpacing = 180, levelGap = 150) => {
    const positions = [];
    if (count <= 0) return positions;
    const totalWidth = (count - 1) * horizontalSpacing;
    const startX = parentX - totalWidth / 2;
    for (let i = 0; i < count; i++) {
      positions.push({
        x: startX + i * horizontalSpacing,
        y: parentY + levelGap,
      });
    }
    return positions;
  };


  // Normalize different provider payloads (GitHub, HuggingFace, Anthropic, SendGrid, OpenAI, ...)
  const normalizeInsight = (raw) => {
    if (!raw) return {};

    // Handle nested insights_json_dump structure
    const data = raw.insights_json_dump || raw;
    
    const provider = data.provider || raw.provider || raw.metadata?.provider || 'Unknown';
    const tokenUser = data.user || data.token_user || raw.token_user || raw.username || raw.name || raw.metadata?.owner || 'Unknown';
    const tokenType = data.token_type || raw.token_type || raw.key_type || 'Unknown';
    const tokenExpiration = data.token_expiration || raw.token_expiration || raw.expires || raw.metadata?.expiration || null;
    const daysRemaining = data.days_remaining || raw.days_remaining || null;

    // GitHub style
    const repositories = Array.isArray(data.repositories) ? data.repositories : [];

    // Scopes
    // - GitHub simple: string[] or {scope}
    // - SendGrid complex: scope objects
    // - OpenAI: { scope, endpoints[], permission }
    const scopes = Array.isArray(data.scopes)
      ? data.scopes.map((s) => ({
          scope: s.scope || s,
          in_scope: s.in_scope ?? true,
          endpoints: Array.isArray(s.endpoints) ? s.endpoints : [],
          permission: s.permission || s.permissions || null,
        }))
      : [];

    // HuggingFace models
    const models = Array.isArray(data.accessible_models)
      ? data.accessible_models.map((m) => ({
          name: m.name,
          private: !!m.private,
          read: m.read,
          write: m.write,
        }))
      : [];

    // Anthropic resources (models)
    const resources = Array.isArray(data.resources)
      ? data.resources.map((r) => ({ id: r.id, name: r.name, type: r.type }))
      : [];

    // Orgs (HF)
    const organizations = Array.isArray(data.organizations) ? data.organizations : [];

    // Postman workspaces
    const workspaces = Array.isArray(data.workspaces) ? data.workspaces : [];

    // Postman roles
    const roles = Array.isArray(data.roles) ? data.roles : [];

    // Postman team info
    const teamInfo = data.team_info || {};

    // Postman user info
    const userInfo = data.user_info || {};

    // S3/AWS specific fields
    const affectedBuckets = Array.isArray(data.affected_buckets) ? data.affected_buckets : [];
    const awsServices = Array.isArray(data.aws_services) ? data.aws_services : [];
    const iamPermissions = Array.isArray(data.iam_permissions) ? data.iam_permissions : [];
    const accessKeyInfo = data.access_key_info || {};

    const totals = {
      total_repositories: data.total_repositories || raw.total_repositories || repositories.length,
      total_scopes: data.total_scopes || raw.total_scopes || scopes.length,
      total_models: data.total_models || raw.total_models || models.length || resources.filter(r => r.type === 'model').length,
      total_organizations: data.total_organizations || raw.total_organizations || organizations.length,
      total_workspaces: data.total_workspaces || raw.total_workspaces || workspaces.length,
      total_roles: data.total_roles || raw.total_roles || roles.length,
      total_buckets: data.total_buckets || raw.total_buckets || affectedBuckets.length,
      total_services: data.total_services || raw.total_services || awsServices.length,
      total_permissions: data.total_permissions || raw.total_permissions || iamPermissions.length,
    };

    return {
      provider,
      token_user: tokenUser,
      token_type: tokenType,
      token_type_short: typeof tokenType === 'string' && tokenType.length > 20 ? `${tokenType.slice(0, 20)}…` : tokenType,
      token_expiration: tokenExpiration,
      days_remaining: daysRemaining,
      repositories,
      scopes,
      models,
      resources,
      organizations,
      workspaces,
      roles,
      team_info: teamInfo,
      user_info: userInfo,
      // S3/AWS specific
      affected_buckets: affectedBuckets,
      aws_services: awsServices,
      iam_permissions: iamPermissions,
      access_key_info: accessKeyInfo,
      // Slack extras (if present on payload)
      slack_url: data.url || raw.url || raw.slack_url || null,
      slack_team_name: data.team || raw.team || raw.team_name || null,
      slack_user_name: data.user || raw.user || raw.slack_user || null,
      slack_team_id: data.team_id || raw.team_id || null,
      slack_user_id: data.user_id || raw.user_id || null,
      ...totals,
    };
  };

  const getConnectionKey = (node1, node2) => {
    return `${node1}-${node2}`;
  };

  export {toSlug, placeChildrenTree, normalizeInsight, getConnectionKey};